import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { AgentRole } from '../../models/Support/SupportEnums';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

interface UseAgentConfigMutationOptions {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

interface UpdateAgentConfigMutationInput {
	id: string;
	isAvailable: boolean;
	maxTickets: number | null;
	role: AgentRole;
}

export interface ISupportAgentConfig {
	id: string;
	mailboxId: string;
	agentId: string;
	agentName?: string;
	agentEmail?: string;
	agentProfilePic?: string;
	isAvailable: boolean;
	maxTickets?: number | null;
	role?: AgentRole;
}

export interface IMyAgentConfig {
	role: AgentRole | 'none';
	isAgent: boolean;
	isAvailable: boolean;
}

export function useMyAgentConfigQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId),
		staleTime: 30_000,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IMyAgentConfig>(SUPPORT_PATHS.mailboxes.myAgent(mailboxId), 'GET'),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load your agent settings' },
	});
}

export function useAgentConfigsQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden(
				() =>
					userSession.webServiceHelper.callAsync<{ values: ISupportAgentConfig[] }>(
						SUPPORT_PATHS.mailboxes.agents(mailboxId),
						'GET'
					),
				{ values: [] }
			),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load support team' },
	});
}

export function useAddAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (agent: { agentId: string; agentName: string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agents(mailboxId), 'POST', agent),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useUpdateAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }: UpdateAgentConfigMutationInput) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agent(mailboxId, id), 'PUT', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			// An agent updating their own row (e.g. toggling availability) must also refresh
			// the current user's `me` config, which other surfaces read for "am I available".
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useDeleteAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agent(mailboxId, id), 'DELETE'),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}
